import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@design-system/Popover/Popover";
import type {
    DataGridListEditorProps,
    DataGridListPopoverEdit,
} from "../../../columns/listEditors";
import { ListEditTrigger } from "../ListEditTrigger";
import { toCloseAndCommit } from "../listEditActions";

interface PopoverListEditorProps<
    TData,
    TValue,
> extends DataGridListEditorProps<TData> {
    edit: DataGridListPopoverEdit<TData, TValue>;
}

type PopoverListBodyProps<TData, TValue> = Pick<
    PopoverListEditorProps<TData, TValue>,
    "row" | "onOpenChange" | "onCloseAndCommit" | "edit"
>;

/** The column's `content`, as its own component: only an open popover mounts it. */
const PopoverListBody = <TData, TValue>({
    row,
    onOpenChange,
    onCloseAndCommit,
    edit,
}: Readonly<PopoverListBodyProps<TData, TValue>>) =>
    edit.content({
        row,
        value: edit.value(row),
        commit: toCloseAndCommit({ edit, row, onCloseAndCommit }),
        close: () => onOpenChange(false),
    });

/** `col.edit.popover`: the column's own content in a popover on the cell, named after the column. */
export const PopoverListEditor = <TData, TValue>({
    row,
    columnLabel,
    trigger,
    isOpen,
    onOpenChange,
    onCloseAndCommit,
    edit,
}: Readonly<PopoverListEditorProps<TData, TValue>>) => (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
        <PopoverTrigger>
            <ListEditTrigger {...trigger} />
        </PopoverTrigger>
        <PopoverContent align="start" aria-label={columnLabel}>
            <PopoverListBody
                row={row}
                onOpenChange={onOpenChange}
                onCloseAndCommit={onCloseAndCommit}
                edit={edit}
            />
        </PopoverContent>
    </Popover>
);
